import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { biz } from '../../lib/api';
import { Card, StatusBadge } from './BusinessLayout';
import { PlusCircle, Zap, Users, BookOpen } from 'lucide-react';

export default function BusinessDashboard() {
  const [today, setToday] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, k, c] = await Promise.all([
          biz.dashboardToday(),
          biz.complaintKpis(),
          biz.listComplaints({ limit: 10 }),
        ]);
        setToday(t); setKpi(k); setComplaints(c);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6 text-ink/60">Loading dashboard…</div>;

  return (
    <div className="grid gap-4" data-testid="biz-dashboard-root">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#D35400]">Today · {today?.date}</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl">Business Dashboard</h1>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Link to="/business/daybook?new=1" className="btn-primary" data-testid="biz-new-activity-cta">
            <PlusCircle className="w-4 h-4"/> New Activity
          </Link>
          <Link to="/business/complaints?new=1" className="btn-primary !bg-[#B71C1C] !border-[#B71C1C]" data-testid="biz-new-complaint-cta">
            <Zap className="w-4 h-4"/> New Complaint
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        <Card testid="biz-card-total-activities" title="Total activities" value={today.total_activities}/>
        <Card testid="biz-card-completed" title="Completed" value={today.completed_activities} tone="good"/>
        <Card testid="biz-card-pending" title="Pending" value={today.pending_activities} tone="warn"/>
        <Card testid="biz-card-followups-today" title="Follow-ups today" value={today.followups_today} tone="info"/>
        <Card testid="biz-card-new-customers" title="New customers" value={today.new_customers}/>
        <Card testid="biz-card-payments" title="Payments (₹)" value={money(today.payments_inr)} tone="good"/>
        <Card testid="biz-card-expenses" title="Expenses (₹)" value={money(today.expenses_inr)} tone="warn"/>
        <Card testid="biz-card-overdue-followups" title="Overdue follow-ups" value={today.overdue_followups} tone="bad"/>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/50 mt-2">Complaint KPIs</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-1">
          <Card testid="biz-kpi-open" title="Open complaints" value={kpi.open_complaints}/>
          <Card testid="biz-kpi-new" title="New today" value={kpi.new_today}/>
          <Card testid="biz-kpi-resolved" title="Resolved today" value={kpi.resolved_today} tone="good"/>
          <Card testid="biz-kpi-closed" title="Closed today" value={kpi.closed_today}/>
          <Card testid="biz-kpi-breached" title="SLA breached" value={kpi.sla_breached} tone="bad"/>
          <Card testid="biz-kpi-critical" title="Critical open" value={kpi.critical_open} tone="bad"/>
          <Card testid="biz-kpi-avg-res" title="Avg resolution (hrs, 30 d)" value={kpi.avg_resolution_hours}/>
          <Card testid="biz-kpi-staff-count" title="Active staff" value={kpi.staff_performance?.length || 0} tone="info"/>
        </div>
      </div>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="card-soft p-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#0D47A1]"/>
            <div className="font-heading font-bold">Recent complaints</div>
            <Link to="/business/complaints" className="ml-auto text-xs text-[#0D47A1]">View all →</Link>
          </div>
          <div className="mt-2 grid gap-1">
            {complaints.map((c) => (
              <Link to={`/business/complaints/${c.complaint_number}`}
                key={c.complaint_number}
                data-testid={`biz-recent-complaint-${c.complaint_number}`}
                className="border border-border rounded-lg px-2 py-1.5 flex items-center gap-2 hover:bg-[#FFF8EE]">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{c.complaint_number} · {c.customer_name}</div>
                  <div className="text-xs text-ink/60 truncate">{c.product} — {c.description}</div>
                </div>
                <StatusBadge value={c.status}/>
              </Link>
            ))}
            {!complaints.length && <div className="text-xs text-ink/50">No complaints yet.</div>}
          </div>
        </div>
        <div className="card-soft p-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0D47A1]"/>
            <div className="font-heading font-bold">Staff activity (today)</div>
          </div>
          <div className="mt-2">
            {(today.activities_by_employee || []).map((e) => (
              <div key={e.employee} className="flex items-center justify-between py-1 text-sm border-b border-border/50 last:border-0">
                <span>{e.employee}</span>
                <span className="font-mono tabular-nums text-xs text-ink/60">{e.count}</span>
              </div>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest text-ink/50 mt-4">Staff complaint performance</div>
          <div className="mt-1 max-h-40 overflow-auto">
            {(kpi.staff_performance || []).map((s) => (
              <div key={s.employee || 'unassigned'} className="flex items-center justify-between py-1 text-xs border-b border-border/50 last:border-0">
                <span>{s.employee}</span>
                <span className="text-ink/60">total {s.total} · res {s.resolved} · pen {s.pending} · brk {s.breached}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function money(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
